import { siteApiSlice } from "../../../../app/siteApiSlice";
import { AdminReportsResponse, AdminReportsRowBase } from "../adminReportsApiSlice";

export interface UsageSummaryRow {
	displayName: string;
	value: string;
}

export interface UsageSummaryResponse {
	lastUpdateDate?: Date;
	response: UsageSummaryRow[];
}

export interface EssaysAndDraftsRow extends AdminReportsRowBase {
	essayCount: number;
	draftCount: number;
	averageNumberOfDraftsPerEssay: number;
}

export interface EssaysAndDraftsResponse {
	lastUpdateDate?: Date;
	response: AdminReportsResponse<EssaysAndDraftsRow>;
}

export interface DraftUsageRow extends AdminReportsRowBase {
	aug: number;
	sep: number;
	oct: number;
	nov: number;
	dec: number;
	jan: number;
	feb: number;
	mar: number;
	apr: number;
	may: number;
	jun: number;
	jul: number;
	total: number;
}

export interface DraftUsageResponse {
	lastUpdateDate?: Date;
	response: AdminReportsResponse<DraftUsageRow>;
}

export interface PeerReviewUsageRow extends AdminReportsRowBase {
	peerReviewRequestedCount: number;
	peerReviewCompletedCount: number;
	peerReviewCompletionPercentage: number;
}

export interface PeerReviewUsageResponse {
	lastUpdateDate?: Date;
	response: AdminReportsResponse<PeerReviewUsageRow>;
}

export interface LessonUsageRow extends AdminReportsRowBase {
	timeSpent: string;
	totalMinutes: number;
}

export interface LessonUsageResponse {
	lastUpdateDate?: Date;
	response: AdminReportsResponse<LessonUsageRow>;
}

export interface LessonUsageByCategoryRow {
	mostRecentUse?: Date;
	tutorialCategoryName: string;
	tutorialCategoryId: number;
	timeSpent: string;
	totalMinutes: number;
}

export interface LessonUsageByCategoryResponse {
	lastUpdateDate?: Date;
	response: AdminReportsResponse<LessonUsageByCategoryRow>;
}

export interface LessonUsageByCategoryAndLessonRow {
	mostRecentUse?: Date;
	lesson: string;
	timeSpent: string;
	totalMinutes: number;
}

export interface LessonUsageByCategoryAndLessonResponse {
	lastUpdateDate?: Date;
	response: AdminReportsResponse<LessonUsageByCategoryAndLessonRow>;
}

export const adminUsageReportApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		usageSummary: build.query<UsageSummaryResponse, { districtId?: number; schoolId?: number }>(
			{
				query: (arg) => {
					const { districtId, schoolId } = arg;
					return {
						url: `/AdminReports/UsageSummaryAjaxHandler`,
						params: { districtId, schoolId },
					};
				},
			}
		),
		essaysAndDrafts: build.query<
			EssaysAndDraftsResponse,
			{ districtId?: number; schoolId?: number }
		>({
			query: (arg) => {
				const { districtId, schoolId } = arg;
				return {
					url: `/AdminReports/EssaysAndDraftsAjaxHandler`,
					params: { districtId, schoolId },
				};
			},
		}),
		draftUsageOverTime: build.query<
			DraftUsageResponse,
			{ districtId?: number; schoolId?: number; schoolYear?: number }
		>({
			query: (arg) => {
				const { districtId, schoolId, schoolYear } = arg;
				return {
					url: `/AdminReports/DraftUsageAjaxHandler`,
					params: { districtId, schoolId, schoolYear },
				};
			},
		}),
		peerReviewUsage: build.query<
			PeerReviewUsageResponse,
			{ districtId?: number; schoolId?: number }
		>({
			query: (arg) => {
				const { districtId, schoolId } = arg;
				return {
					url: `/AdminReports/PeerReviewUsageAjaxHandler`,
					params: { districtId, schoolId },
				};
			},
		}),
		lessonUsage: build.query<LessonUsageResponse, { districtId?: number; schoolId?: number }>({
			query: (arg) => {
				const { districtId, schoolId } = arg;
				return {
					url: `/AdminReports/LessonUsageAjaxHandler`,
					params: { districtId, schoolId },
				};
			},
		}),
		lessonUsageByCategory: build.query<
			LessonUsageByCategoryResponse,
			{ districtId?: number; schoolId?: number }
		>({
			query: (arg) => {
				const { districtId, schoolId } = arg;
				return {
					url: `/AdminReports/LessonUsageByCategoryAjaxHandler`,
					params: { districtId, schoolId },
				};
			},
		}),
		lessonUsageByCategoryAndLesson: build.query<
			LessonUsageByCategoryAndLessonResponse,
			{ tutorialCategoryId: number; districtId?: number; schoolId?: number }
		>({
			query: (arg) => {
				const { tutorialCategoryId, districtId, schoolId } = arg;
				return {
					url: `/AdminReports/LessonUsageByCategoryAndLessonAjaxHandler`,
					params: { tutorialCategoryId, districtId, schoolId },
				};
			},
		}),
	}),
});

export const {
	useLazyUsageSummaryQuery,
	useLazyEssaysAndDraftsQuery,
	useDraftUsageOverTimeQuery,
	useLazyDraftUsageOverTimeQuery,
	useLazyPeerReviewUsageQuery,
	useLazyLessonUsageQuery,
	useLazyLessonUsageByCategoryQuery,
	useLazyLessonUsageByCategoryAndLessonQuery,
} = adminUsageReportApiSlice;
